import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Phone, CheckCircle2 } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { ServiceCard } from '../components/ServiceCard';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE = "https://images.unsplash.com/photo-1761658652336-95d31ce1d806?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxtaWQlMjBjZW50dXJ5JTIwbW9kZXJuJTIwaW50ZXJpb3IlMjByZW5vdmF0aW9ufGVufDB8fHx8MTc2OTg3NjU1NHww&ixlib=rb-4.1.0&q=85&w=1600";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_construction-hub-125/artifacts/hiq4o5va_Logo%20001%20-%20TR%20Kenscho%20Design.png";

export default function Home() {
  const [services, setServices] = useState([]);
  const { t, language, getLocalizedLink } = useLanguage();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    };
    fetchServices();
  }, []);

  const serviceAreas = language === 'fr' 
    ? ['Lanaudière', 'Laurentides', 'Laval', 'Montréal']
    : ['Lanaudière', 'Laurentians', 'Laval', 'Montreal'];

  return (
    <div className="grain-texture" data-testid="home-page">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{language === 'fr' 
          ? 'Kenscho Design | Construction et Rénovation | Lanaudière, Laurentides, Laval, Montréal'
          : 'Kenscho Design | Construction & Renovation | Lanaudière, Laurentians, Laval, Montreal'}
        </title>
        <meta name="description" content={language === 'fr'
          ? 'Services de construction et rénovation de qualité pour projets résidentiels, commerciaux et industriels à Lanaudière, Laurentides, Laval et Montréal. Location d\'équipement, excavation, béton et plus.'
          : 'Quality construction and renovation services for residential, commercial, and industrial projects in Lanaudière, Laurentians, Laval and Montreal. Equipment rental, excavation, concrete and more.'} />
        <link rel="canonical" href={`https://www.kenscho.ca${getLocalizedLink('/')}`} />
        
        {/* Hreflang Tags for SEO */}
        <link rel="alternate" hreflang="en" href="https://www.kenscho.ca/" />
        <link rel="alternate" hreflang="fr" href="https://www.kenscho.ca/fr" />
        <link rel="alternate" hreflang="x-default" href="https://www.kenscho.ca/" />
        
        {/* Open Graph */}
        <meta property="og:title" content={language === 'fr' ? 'Kenscho Design | Construction et Rénovation' : 'Kenscho Design | Construction & Renovation'} />
        <meta property="og:description" content={language === 'fr'
          ? 'Services de construction et rénovation de qualité à Lanaudière, Laurentides, Laval et Montréal.'
          : 'Quality construction and renovation services in Lanaudière, Laurentians, Laval and Montreal.'} />
        <meta property="og:url" content={`https://www.kenscho.ca${getLocalizedLink('/')}`} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={language === 'fr' ? 'fr_CA' : 'en_CA'} />
        <meta property="og:locale:alternate" content={language === 'fr' ? 'en_CA' : 'fr_CA'} />
      </Helmet>

      {/* Hero Section */}
      <section className="min-h-screen relative flex items-center pt-20">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/60" />
          <img 
            src={HERO_IMAGE}
            alt="Modern interior renovation"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-20 py-20">
          <div className="max-w-2xl">
            {/* Logo at top left */}
            <img 
              src={LOGO_URL}
              alt="Kenscho Design"
              className="h-24 md:h-32 w-auto mb-8 animate-fade-in-up"
            />
            
            <p className="text-sm font-medium tracking-widest uppercase text-primary mb-6 animate-fade-in-up">
              {t('home.tagline')}
            </p>
            
            <h1 className="text-5xl md:text-7xl font-light tracking-tight leading-[1.1] mb-8 animate-fade-in-up animation-delay-100">
              {t('home.heroTitle1')}<br />
              <span className="text-primary">{t('home.heroTitle2')}</span>
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-10 animate-fade-in-up animation-delay-200">
              {t('home.heroDesc')}
            </p>
            
            {/* Service Areas */}
            <div className="flex flex-wrap items-center gap-4 mt-12 animate-fade-in-up animation-delay-400">
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="h-4 w-4 text-primary" />
                <span className="text-sm">{t('home.serving')}</span>
              </div>
              {serviceAreas.map((area) => (
                <span key={area} className="text-sm px-3 py-1 bg-muted rounded-full">
                  {area}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Divisions Section */}
      <section className="py-6" data-testid="divisions-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light tracking-tight">
              {language === 'fr' ? 'Solutions complètes pour vos projets' : 'Complete Solutions for Your Projects'}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Construction */}
            <Link to={getLocalizedLink('/services')} className="group">
              <div className="bg-card border border-border/60 rounded-2xl overflow-hidden hover:border-primary/50 transition-all">
                <div className="aspect-[4/3] overflow-hidden bg-muted">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/kij23qw2_IMG_2098.jpeg"
                    alt="Construction"
                    className="w-full h-full object-cover object-center scale-125 transition-transform duration-500 group-hover:scale-[1.35]"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-medium mb-2 group-hover:text-primary transition-colors">
                    {language === 'fr' ? 'Construction' : 'Construction'}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {language === 'fr' 
                      ? 'Services complets de construction résidentielle, commerciale et industrielle. De la rénovation de cuisine aux projets commerciaux majeurs.'
                      : 'Full-service residential, commercial, and industrial construction. From kitchen renovations to major commercial buildouts, we deliver quality craftsmanship.'}
                  </p>
                </div>
              </div>
            </Link>

            {/* Equipment Rental */}
            <Link to={getLocalizedLink('/equipment-rental')} className="group">
              <div className="bg-card border border-border/60 rounded-2xl overflow-hidden hover:border-primary/50 transition-all">
                <div className="aspect-[4/3] overflow-hidden bg-muted">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/g7dlf2ho_0305421_mudmixer-multi-use-mixer-for-concrete-mortar-and-stucco-portable-electric.jpg"
                    alt="Equipment Rental"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-medium mb-2 group-hover:text-primary transition-colors">
                    {language === 'fr' ? 'Location d\'équipement' : 'Equipment Rental'}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {language === 'fr' 
                      ? 'Large gamme d\'outils et d\'équipements professionnels disponibles à la location. Tarifs journaliers, hebdomadaires et mensuels compétitifs.'
                      : 'Wide range of professional tools and equipment available for rent. Competitive daily, weekly, and monthly rates for contractors and DIYers alike.'}
                  </p>
                </div>
              </div>
            </Link>

            {/* Welding */}
            <Link to={getLocalizedLink('/welding')} className="group">
              <div className="bg-card border border-border/60 rounded-2xl overflow-hidden hover:border-primary/50 transition-all">
                <div className="aspect-[4/3] overflow-hidden bg-muted">
                  <img 
                    src="https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=600&q=80"
                    alt="Welding"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-medium mb-2 group-hover:text-primary transition-colors">
                    {language === 'fr' ? 'Soudure' : 'Welding'}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {language === 'fr' 
                      ? 'Services de soudure structurelle et de fabrication. Réparations, modifications et projets sur mesure en acier et métaux.'
                      : 'Structural welding and fabrication services. Repairs, modifications, and custom steel work for construction and industrial projects.'}
                  </p>
                </div>
              </div>
            </Link>

            {/* Transport */}
            <Link to={getLocalizedLink('/transport')} className="group">
              <div className="bg-card border border-border/60 rounded-2xl overflow-hidden hover:border-primary/50 transition-all">
                <div className="aspect-[4/3] overflow-hidden bg-muted">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/4mcfslxg_IMG_6533.jpeg"
                    alt="Transport"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-medium mb-2 group-hover:text-primary transition-colors">
                    {language === 'fr' ? 'Transport' : 'Transport'}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {language === 'fr' 
                      ? 'Services de transport fiables pour équipements et matériaux. Livraison rapide et sécuritaire partout dans la grande région de Montréal.'
                      : 'Reliable transport services for equipment and materials. Fast, safe delivery of machinery and supplies across the Greater Montreal area.'}
                  </p>
                </div>
              </div>
            </Link>

            {/* Concrete */}
            <Link to={getLocalizedLink('/concrete')} className="group">
              <div className="bg-card border border-border/60 rounded-2xl overflow-hidden hover:border-primary/50 transition-all">
                <div className="aspect-[4/3] overflow-hidden bg-muted">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/baxnu0we_Concrete%20Finishing%20Services.jpg"
                    alt="Concrete"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-medium mb-2 group-hover:text-primary transition-colors">
                    {language === 'fr' ? 'Béton' : 'Concrete'}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {language === 'fr' 
                      ? 'Services complets de béton: coulage, coffrage, coupe et finition. Fondations, dalles, trottoirs et travaux décoratifs.'
                      : 'Complete concrete services: pouring, forming, cutting, and finishing. Foundations, slabs, sidewalks, and decorative concrete work.'}
                  </p>
                </div>
              </div>
            </Link>

            {/* Excavation */}
            <Link to={getLocalizedLink('/excavation')} className="group">
              <div className="bg-card border border-border/60 rounded-2xl overflow-hidden hover:border-primary/50 transition-all">
                <div className="aspect-[4/3] overflow-hidden bg-muted">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/ck7x7u9l_IMG_6640.jpeg"
                    alt="Excavation"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-medium mb-2 group-hover:text-primary transition-colors">
                    {language === 'fr' ? 'Excavation' : 'Excavation'}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {language === 'fr' 
                      ? 'Services de terrassement professionnels: préparation de terrain, nivellement, tranchées et installation de services souterrains.'
                      : 'Professional earthmoving services: site prep, grading, trenching, and utility installation. CAT and Kubota equipment for any project size.'}
                  </p>
                </div>
              </div>
            </Link>

            {/* Work-Site - Centered on its own row */}
            <Link to={getLocalizedLink('/work-site')} className="group md:col-start-2">
              <div className="bg-card border border-border/60 rounded-2xl overflow-hidden hover:border-primary/50 transition-all">
                <div className="aspect-[4/3] overflow-hidden bg-muted">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_build-kenscho/artifacts/vez2lt95_IMG_9061-scaled.jpg"
                    alt="Work-Site"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-medium mb-2 group-hover:text-primary transition-colors">
                    {language === 'fr' ? 'Chantier' : 'Work-Site'}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {language === 'fr' 
                      ? 'Équipement essentiel pour votre chantier: génératrices mobiles, goulottes à débris, escaliers temporaires et remorques-bennes.'
                      : 'Essential worksite equipment: mobile generators, garbage chutes, temporary stairs, and dump trailers. Keep your project running smoothly.'}
                  </p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-muted/30" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <p className="text-sm font-medium tracking-widest uppercase text-primary mb-4">
                {t('home.whatWeDo')}
              </p>
              <h2 className="text-4xl md:text-5xl font-light tracking-tight">
                {t('home.ourServices')}
              </h2>
            </div>
            <Button asChild variant="ghost" className="rounded-full" data-testid="view-all-services-btn">
              <Link to={getLocalizedLink('/services')}>
                {t('home.viewAllServices')} <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(0, 6).map((service, index) => (
              <div key={service.id} className="stagger-item">
                <ServiceCard service={service} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-medium tracking-widest uppercase text-primary mb-4">
                {t('home.whyKenscho')}
              </p>
              <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-8">
                {t('home.qualityWork')}<br />{t('home.trustedResults')}
              </h2>
              
              <div className="space-y-6">
                {t('home.whyPoints').map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary shrink-0" />
                    <p className="text-lg text-muted-foreground">{item}</p>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <Button asChild size="lg" className="rounded-full px-8 btn-animate" data-testid="contact-us-btn">
                  <Link to={getLocalizedLink('/contact')}>
                    {t('home.contactToday')} <Phone className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-[4/5] rounded-lg overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1644657711115-ee46e8dd7c7d?w=800"
                  alt="Construction professional"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-primary text-white p-8 rounded-lg max-w-xs">
                <p className="text-3xl font-light mb-2">15+</p>
                <p className="text-white/80">{t('home.yearsExp')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Divisions CTA */}
      <section className="py-24 bg-foreground text-white" data-testid="divisions-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-sm font-medium tracking-widest uppercase text-primary mb-4">
              {t('home.ourDivisions')}
            </p>
            <h2 className="text-4xl md:text-5xl font-light tracking-tight">
              {t('home.moreThanConstruction')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: language === 'fr' ? 'Kenscho Location d\'équipement' : 'Kenscho Equipment Rental',
                description: t('home.divisionEquipment'),
                link: '/equipment-rental',
                testId: 'division-equipment'
              },
              {
                title: language === 'fr' ? 'Kenscho Chantier' : 'Kenscho Work-Site',
                description: t('home.divisionWorksite'),
                link: '/work-site',
                testId: 'division-worksite'
              },
              {
                title: 'Kenscho Transport',
                description: t('home.divisionTransport'),
                link: '/transport',
                testId: 'division-transport'
              }
            ].map((division) => (
              <Link 
                key={division.title}
                to={getLocalizedLink(division.link)}
                className="group p-8 border border-white/20 rounded-lg hover:border-primary transition-colors duration-300"
                data-testid={division.testId}
              >
                <h3 className="text-xl font-medium mb-4 group-hover:text-primary transition-colors">
                  {division.title}
                </h3>
                <p className="text-white/70 mb-6">{division.description}</p>
                <span className="inline-flex items-center text-primary text-sm font-medium">
                  {t('home.learnMore')} <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bg-primary/10 rounded-2xl p-12 md:p-16 text-center">
            <h2 className="text-3xl md:text-4xl font-light tracking-tight mb-6">
              {t('home.readyToStart')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-10">
              {t('home.readyDesc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="rounded-full px-8 btn-animate" data-testid="cta-quote-btn">
                <Link to={getLocalizedLink('/contact')}>{t('home.getFreeQuote')}</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-full px-8 btn-animate">
                <a href="mailto:info@kenscho.ca">info@kenscho.ca</a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
